import { useNavigate, useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { orderToReceipt } from '@mlm/shared';
import { Receipt } from '@mlm/ui';
import { getPosOrder, listBranches } from '../lib/api';
import { useSaleStore } from '../lib/cart-store';
import { checkoutToReceipt } from '../lib/receipt-data';

/**
 * The sale's receipt on a page of its own, where the checkout lands.
 *
 * Straight after a sale the tape is drawn from the checkout response held in
 * the sale store (it carries the activation PINs and the change as they were
 * at the moment of sale). After a reload that response is gone, so the page
 * falls back to fetching the order and the shared reprint mapping.
 */
export function ReceiptPage() {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const stored = useSaleStore((s) => s.receipt);
  const mode = useSaleStore((s) => s.mode);
  const fromSale = stored && stored.order.id === orderId ? stored : null;

  const branchesQuery = useQuery({ queryKey: ['branches'], queryFn: listBranches });
  const orderQuery = useQuery({
    queryKey: ['pos-order', orderId],
    queryFn: () => getPosOrder(orderId!),
    enabled: !fromSale && !!orderId,
  });

  const data = fromSale
    ? checkoutToReceipt(
        fromSale,
        branchesQuery.data?.find((b) => b.id === fromSale.order.branchId),
      )
    : orderQuery.data
      ? orderToReceipt(orderQuery.data)
      : null;

  return (
    <div className="mx-auto max-w-2xl">
      <h1 className="no-print mb-4 text-center text-2xl font-semibold">Sales Invoice</h1>
      {data ? (
        <Receipt
          data={data}
          touch
          // `resume`: straight on to the next transfer, without the stockist
          // tab's "this is a transfer" confirmation all over again.
          onNew={() =>
            navigate(mode === 'stockist' ? '/stockist' : '/', { state: { resume: true } })
          }
        />
      ) : (
        <p className="text-center text-base text-muted-foreground">
          {orderQuery.isError ? 'This sale could not be loaded.' : 'Loading…'}
        </p>
      )}
    </div>
  );
}
